import React, { useState, useEffect, useCallback, useRef } from "react";
import { X, ArrowLeft, BadgeCheck } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { getFriends } from "../../services/followService";
import FetchErrorState from "../Common/FetchErrorState";
import { useNavigate } from "react-router-dom";

const FriendsModal = ({ open, onClose }) => {
  const { apiCall } = useAuth();
  const navigate = useNavigate();

  const [friends, setFriends] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const observerTarget = useRef(null);

  // Helper function to get profile picture
  const getProfilePicture = (profilePic) => {
    // If it's an object with sizes, use thumbnail or small
    if (profilePic && typeof profilePic === "object") {
      return profilePic.thumbnail || profilePic.small || profilePic.medium;
    }

    // If it's a string (old format), use it directly
    if (typeof profilePic === "string") {
      return profilePic;
    }

    return null;
  };

  // Check if screen is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Fetch friends function
  const fetchFriends = useCallback(
    async (isInitial = false) => {
      if (isLoading || (!hasMore && !isInitial)) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await getFriends(
          apiCall,
          15,
          isInitial ? null : cursor
        );

        if (response.success) {
          setFriends((prev) =>
            isInitial ? response.friends : [...prev, ...response.friends]
          );
          setCursor(response.nextCursor);
          setHasMore(response.hasMore);
        }
      } catch (err) {
        setError(err.message || "Failed to load friends");
      } finally {
        setIsLoading(false);
      }
    },
    [apiCall, cursor, hasMore, isLoading]
  );

  // Initial load
  useEffect(() => {
    if (open) {
      setFriends([]);
      setCursor(null);
      setHasMore(true);
      setError(null);
      fetchFriends(true);
    }
  }, [open]);

  // Infinite scroll observer
  useEffect(() => {
    if (!open || isLoading || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchFriends();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [open, isLoading, hasMore, fetchFriends]);

  const handleProfileClick = (username) => {
    navigate(`/${username}`);
    onClose();
  };

  // Get initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(/\s+/);
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  };

  if (!open) return null;

  // Mobile layout
  if (isMobile) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex flex-col">
        {/* Mobile Header with Back Arrow */}
        <div className="border-b border-gray-200 px-4 py-3">
          <div className="flex items-center">
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors mr-3"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-bold text-gray-900">Friends</h2>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && friends.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : error && friends.length === 0 ? (
            <FetchErrorState message={error} onRetry={() => fetchFriends(true)} />
          ) : friends.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <p className="text-gray-500 text-sm">
                No friends yet. Start following people to see mutual connections
                here!
              </p>
            </div>
          ) : (
            // Friends List
            <div className="py-2">
              {friends.map((friend) => (
                <div
                  key={friend._id}
                  onClick={() => handleProfileClick(friend.username)}
                  className="flex items-center gap-2 px-4 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  {/* Profile Picture */}
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 p-[2px] flex-shrink-0">
                    <div className="w-full h-full rounded-full bg-white p-[1px] overflow-hidden">
                      {(() => {
                        const profilePictureUrl = getProfilePicture(
                          friend.profilePicture
                        );
                        return profilePictureUrl ? (
                          <img
                            src={profilePictureUrl}
                            alt={friend.name}
                            className="w-full h-full object-cover rounded-full"
                          />
                        ) : (
                          <div className="w-full h-full rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                            <span className="text-slate-600 font-semibold text-sm">
                              {getInitials(friend.name)}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0 flex items-center">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {friend.username}
                    </p>
                    {friend.verified && <BadgeCheck className="w-4 h-4 ml-1 fill-[#b0d284] stroke-white" />}
                  </div>
                </div>
              ))}

              {/* Loading indicator for infinite scroll */}
              {isLoading && friends.length > 0 && (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                </div>
              )}

              {/* Observer target for infinite scroll */}
              <div ref={observerTarget} className="h-4" />

              {/* End Message */}
              {!hasMore && friends.length > 0 && (
                <div className="flex justify-center py-4">
                  <p className="text-xs text-gray-400">
                    You've seen all your friends
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div
        className="bg-white rounded-2xl w-full max-w-md flex flex-col shadow-2xl"
        style={{ height: "480px" }}
      >
        {/* Header */}
        <div className="p-4 flex-shrink-0 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Friends</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content - Fixed height with scrolling */}
        <div className="flex-1 overflow-y-auto">
          {/* Error State */}
          {error && (
            <div className="flex justify-center items-center py-12 px-4">
              <p className="text-red-500 text-sm text-center">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {isLoading && friends.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : friends.length === 0 && !error ? (
            // Empty State
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <p className="text-gray-500 text-sm">
                No friends yet. Start following people to see mutual connections
                here!
              </p>
            </div>
          ) : (
            // Friends List
            <div className="py-2">
              {friends.map((friend) => (
                <div
                  key={friend._id}
                  onClick={() => handleProfileClick(friend.username)}
                  className="flex items-center gap-2 px-4 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  {/* Profile Picture */}
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 p-[2px] flex-shrink-0">
                    <div className="w-full h-full rounded-full bg-white p-[1px] overflow-hidden">
                      {(() => {
                        const profilePictureUrl = getProfilePicture(
                          friend.profilePicture
                        );
                        return profilePictureUrl ? (
                          <img
                            src={profilePictureUrl}
                            alt={friend.name}
                            className="w-full h-full object-cover rounded-full"
                          />
                        ) : (
                          <div className="w-full h-full rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                            <span className="text-slate-600 font-semibold text-sm">
                              {getInitials(friend.name)}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* User Info */}
                  {/* <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      @{friend.username}
                    </p>
                    {friend.name && (
                      <p className="text-xs text-gray-500 truncate">
                        {friend.name}
                      </p>
                    )}
                  </div> */}
                  {/* User Info */}
                  <div className="flex-1 min-w-0 flex items-center">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {friend.username}
                    </p>
                    {friend.verified && <BadgeCheck className="w-4 h-4 ml-1 fill-[#b0d284] stroke-white" />}
                  </div>
                </div>
              ))}

              {/* Loading indicator for infinite scroll */}
              {isLoading && friends.length > 0 && (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                </div>
              )}

              {/* Observer target for infinite scroll */}
              <div ref={observerTarget} className="h-4" />

              {/* End Message */}
              {!hasMore && friends.length > 0 && (
                <div className="flex justify-center py-4">
                  <p className="text-xs text-gray-400">
                    You've seen all your friends
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer - Close Button */}
        <div className="border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-3 text-base sm:text-lg text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default FriendsModal;
